import { format, isValid, parse } from 'date-fns';
import { Art } from 'types/art';
import { Manufacturer } from 'types/manufacturer';
import { Location } from 'types/washhub';
import { SKU } from 'types/sku';
import { PurchaseOrderPdfData } from 'components/pdfs/PurchaseOrderPdf.types';
import {
  CreatePurchaseOrderApiPayload,
  PurchaseOrder,
} from 'types/purchaseOrder';
import {
  formatManufacturerEmails,
  getPurchaseOrderManufacturerEmails,
  parseEmailList,
} from 'utils/manufacturer';
export type CreatePurchaseOrderSkuLineValues = {
  skuId: string;
  artNumber: string;
  artId: string;
  artDescription: string;
  boxQty: number | '';
  totalUnits: number | '';
  costPerBox: number | '';
  costPerUnit: number | '';
  totalCost: number | '';
};

export type CreatePurchaseOrderFormValues = {
  date: Date | string | null;
  manufacturerId: string;
  manufacturerAddress: string;
  manufacturerAddressLine1: string;
  manufacturerAddressLine2: string;
  manufacturerCity: string;
  manufacturerState: string;
  manufacturerZip: string;
  manufacturerContact: string;
  destinationHubId: string;
  manualDestinationHubName: string;
  isManualDestinationHub: boolean;
  shipToAddressLine1: string;
  shipToAddressLine2: string;
  shipToCity: string;
  shipToState: string;
  shipToZip: string;
  shipToContactId: string;
  manualShipToContact: string;
  isManualShipToContact: boolean;
  poNumber: string;
  liftGateRequired: boolean;
  shippingNotes: string;
  shippingCost: number | '';
  tax: number | '';
  costAdjustment: number | '';
  skuLines: CreatePurchaseOrderSkuLineValues[];
};

export const getEmptySkuLine = (): CreatePurchaseOrderSkuLineValues => ({
  skuId: '',
  artNumber: '',
  artId: '',
  artDescription: '',
  boxQty: '',
  totalUnits: '',
  costPerBox: '',
  costPerUnit: '',
  totalCost: '',
});
export const NA_ART_NUMBER = 'N/A';

export const getSkuArtNumbers = (sku?: { artNumbers?: string[] } | null) => {
  const artNumbers = sku?.artNumbers ?? [];

  return artNumbers.length > 0 ? artNumbers : [NA_ART_NUMBER];
};

export const hasMoqPricing = (moq?: Record<string, number>) =>
  Object.values(moq || {}).some((price) => Number(price) > 0);

export const getMoqPrice = (quantity: number, moq?: Record<string, number>) => {
  if (!moq || quantity <= 0) {
    return 0;
  }

  const thresholds = Object.keys(moq)
    .map(Number)
    .filter((threshold) => !Number.isNaN(threshold))
    .sort((a, b) => a - b);

  if (thresholds.length === 0) {
    return 0;
  }

  let selectedThreshold = thresholds[0];

  for (const threshold of thresholds) {
    if (quantity >= threshold) {
      selectedThreshold = threshold;
    } else {
      break;
    }
  }

  return Number(moq[String(selectedThreshold)]) || 0;
};

export const getCostPerBoxFromMoq = (
  boxQty: number,
  moq?: Record<string, number>,
) => getMoqPrice(boxQty, moq);

type SkuLinePricing = Pick<
  SKU,
  | 'manufacturerItemsInBox'
  | 'manufacturerBoxCostPerMoq'
  | 'manufacturerUnitCostPerMoq'
>;

export const applySkuLineCalculations = (
  line: CreatePurchaseOrderSkuLineValues,
  sku?: SkuLinePricing | null,
  source: 'boxQty' | 'totalUnits' | 'sku' = 'boxQty',
): CreatePurchaseOrderSkuLineValues => {
  let boxQty = line.boxQty === '' ? 0 : Number(line.boxQty);
  let totalUnits = line.totalUnits === '' ? 0 : Number(line.totalUnits);
  const unitsPerBox = sku?.manufacturerItemsInBox ?? 0;

  if (source === 'totalUnits') {
    if (unitsPerBox > 0 && totalUnits > 0) {
      boxQty = Math.ceil(totalUnits / unitsPerBox);
      totalUnits = boxQty * unitsPerBox;
    } else {
      boxQty = 0;
      totalUnits = 0;
    }
  } else if (unitsPerBox > 0 && boxQty > 0) {
    totalUnits = boxQty * unitsPerBox;
  } else {
    totalUnits = 0;
  }

  let costPerBox = 0;
  let costPerUnit = 0;
  let totalCost = 0;

  if (hasMoqPricing(sku?.manufacturerBoxCostPerMoq)) {
    costPerBox = getMoqPrice(boxQty, sku?.manufacturerBoxCostPerMoq);
    totalCost = boxQty * costPerBox;
    costPerUnit = totalUnits > 0 ? totalCost / totalUnits : 0;
  } else if (hasMoqPricing(sku?.manufacturerUnitCostPerMoq)) {
    costPerUnit = getMoqPrice(totalUnits, sku?.manufacturerUnitCostPerMoq);
    totalCost = totalUnits * costPerUnit;
    costPerBox = boxQty > 0 ? totalCost / boxQty : 0;
  }

  return {
    ...line,
    boxQty: boxQty > 0 ? boxQty : '',
    totalUnits: totalUnits > 0 ? totalUnits : '',
    costPerBox: costPerBox > 0 ? Number(costPerBox.toFixed(2)) : '',
    costPerUnit: costPerUnit > 0 ? Number(costPerUnit.toFixed(4)) : '',
    totalCost: totalCost > 0 ? Number(totalCost.toFixed(2)) : '',
  };
};

export const getSkuLinesSubtotal = (
  skuLines: CreatePurchaseOrderSkuLineValues[],
) =>
  skuLines.reduce((sum, line) => {
    const totalCost = line.totalCost === '' ? 0 : Number(line.totalCost);
    return sum + totalCost;
  }, 0);

export const toCostNumber = (value?: number | string | '' | null) => {
  if (value === '' || value === undefined || value === null) {
    return 0;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : Number(parsed.toFixed(2));
};

export const isPurchaseOrderFormValid = (
  values: CreatePurchaseOrderFormValues,
) => {
  const hasDestination = values.isManualDestinationHub
    ? Boolean(values.manualDestinationHubName.trim())
    : Boolean(values.destinationHubId);

  const hasShipToContact = values.isManualShipToContact
    ? Boolean(values.manualShipToContact.trim())
    : Boolean(values.shipToContactId);

  const hasValidSkuLines = values.skuLines.some(
    (line) =>
      line.skuId &&
      line.artId &&
      line.artNumber &&
      line.boxQty !== '' &&
      Number(line.boxQty) > 0,
  );

  return Boolean(
    values.date &&
      values.manufacturerId &&
      values.poNumber.trim() &&
      hasDestination &&
      values.shipToAddressLine1.trim() &&
      values.shipToCity.trim() &&
      values.shipToState.trim() &&
      values.shipToZip.trim() &&
      hasShipToContact &&
      hasValidSkuLines,
  );
};

export const getShipToContactDetails = (
  values: CreatePurchaseOrderFormValues,
  washHubAdmins: Array<{
    _id: string;
    fname?: string;
    lname?: string;
    phone?: string;
    email?: string;
  }>,
) => {
  if (values.isManualShipToContact) {
    return {
      name: values.manualShipToContact,
      phone: '',
      email: '',
    };
  }

  const admin = washHubAdmins.find(
    (item) => item._id === values.shipToContactId,
  );

  return {
    name: `${admin?.fname ?? ''} ${admin?.lname ?? ''}`.trim(),
    phone: admin?.phone ?? '',
    email: admin?.email ?? '',
  };
};

export const toPurchaseOrderDate = (
  value?: Date | string | null,
): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }

  const parsed = parse(value, 'MM/dd/yyyy', new Date());

  if (isValid(parsed)) {
    return parsed;
  }

  const isoDay = parse(value, 'yyyy-MM-dd', new Date());

  if (isValid(isoDay)) {
    return isoDay;
  }

  const fallback = new Date(value);

  return isValid(fallback) ? fallback : null;
};

export const formatPurchaseOrderDate = (
  value?: Date | string | null,
  pattern = 'MM/dd/yyyy',
) => {
  const date = toPurchaseOrderDate(value);

  return date ? format(date, pattern) : '';
};

export const toPurchaseOrderNoonIso = (value?: Date | string | null) => {
  const date = toPurchaseOrderDate(value);

  if (!date) {
    return undefined;
  }

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    12,
    0,
    0,
    0,
  ).toISOString();
};

export const mapFormValuesToPdfData = (
  values: CreatePurchaseOrderFormValues,
  context: {
    manufacturerName: string;
    destinationHubName: string;
    shipToContact: string;
    shipToContactPhone: string;
    shipToContactEmail: string;
  },
  skus: SKU[],
): PurchaseOrderPdfData => ({
  poNumber: values.poNumber,
  date: formatPurchaseOrderDate(values.date),
  manufacturerName: context.manufacturerName,
  manufacturerAddressLine1: values.manufacturerAddressLine1,
  manufacturerAddressLine2: values.manufacturerAddressLine2,
  manufacturerCity: values.manufacturerCity,
  manufacturerState: values.manufacturerState,
  manufacturerZip: values.manufacturerZip,
  manufacturerContact: values.manufacturerContact,
  destinationHubName: context.destinationHubName,
  shipToAddressLine1: values.shipToAddressLine1,
  shipToAddressLine2: values.shipToAddressLine2,
  shipToCity: values.shipToCity,
  shipToState: values.shipToState,
  shipToZip: values.shipToZip,
  shipToContact: context.shipToContact,
  shipToContactPhone: context.shipToContactPhone,
  shipToContactEmail: context.shipToContactEmail,
  liftGateRequired: values.liftGateRequired,
  shippingNotes: values.shippingNotes,
  subtotal: getSkuLinesSubtotal(values.skuLines),
  shippingCost: toCostNumber(values.shippingCost),
  tax: toCostNumber(values.tax),
  adjustment: toCostNumber(values.costAdjustment),
  skuLines: values.skuLines
    .filter((line) => line.skuId)
    .map((line, index) => {
      const sku = skus.find((item) => item._id === line.skuId);

      return {
        lineNumber: index + 1,
        skuName: sku?.name ?? '',
        artNumber: line.artNumber,
        artDescription: line.artDescription,
        boxQty: line.boxQty === '' ? 0 : Number(line.boxQty),
        totalUnits: line.totalUnits === '' ? 0 : Number(line.totalUnits),
        costPerBox: line.costPerBox === '' ? 0 : Number(line.costPerBox),
        costPerUnit: line.costPerUnit === '' ? 0 : Number(line.costPerUnit),
        totalCost: line.totalCost === '' ? 0 : Number(line.totalCost),
        description: sku?.name ?? '',
        manufacturerDescription: sku?.manufacturerDescription ?? '',
        manufacturerItemsInBox: sku?.manufacturerItemsInBox ?? 0,
        manufacturerSkuId: sku?.manufacturerSkuId ?? '',
      };
    }),
});

export const mapFormValuesToApiPayload = (
  values: CreatePurchaseOrderFormValues,
): CreatePurchaseOrderApiPayload => ({
  poNumer: values.poNumber,
  manufacturerId: values.manufacturerId,
  destinationLocationId: values.isManualDestinationHub
    ? undefined
    : values.destinationHubId,
  manualDestinationHubName: values.isManualDestinationHub
    ? values.manualDestinationHubName
    : undefined,
  status: 'open',
  liftGateRequired: values.isManualDestinationHub
    ? undefined
    : values.liftGateRequired,
  shippingNotes: values.shippingNotes || undefined,
  subtotal: Number(getSkuLinesSubtotal(values.skuLines).toFixed(2)),
  shippingCost: toCostNumber(values.shippingCost),
  tax: toCostNumber(values.tax),
  costAdjustment: toCostNumber(values.costAdjustment),
  date: toPurchaseOrderNoonIso(values.date),
  overrideManufacturerEmails: parseEmailList(values.manufacturerContact),
  locationUpdate:
    !values.isManualDestinationHub && values.destinationHubId
      ? {
          locationId: values.destinationHubId,
          liftGateRequired: values.liftGateRequired,
          purchaseOrderShippingNotes: values.shippingNotes,
        }
      : undefined,
  orderDetails: values.skuLines
    .filter(
      (line) =>
        line.skuId &&
        line.artId &&
        line.boxQty !== '' &&
        Number(line.boxQty) > 0,
    )
    .map((line) => ({
      skuId: line.skuId,
      artId: line.artId,
      boxQuantity: Number(line.boxQty),
      itemQuantity: line.totalUnits === '' ? 0 : Number(line.totalUnits),
    })),
});
export const getNameInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

export const getManufacturerPoSuffix = (manufacturerName?: string) =>
  (manufacturerName ?? '').trim().slice(0, 2).toUpperCase();

export const generatePoNumber = ({
  date,
  hubName,
  userFirstName,
  userLastName,
  manufacturerName,
}: {
  date: Date | string;
  hubName: string;
  userFirstName?: string;
  userLastName?: string;
  manufacturerName?: string;
}) => {
  const datePart = formatPurchaseOrderDate(date, 'MMddyyyy');
  const hubInitials = getNameInitials(hubName);
  const userInitials = `${userFirstName?.[0] ?? ''}${
    userLastName?.[0] ?? ''
  }`.toUpperCase();
  const manufacturerSuffix = getManufacturerPoSuffix(manufacturerName);

  return `${datePart}${hubInitials}${userInitials}${manufacturerSuffix}`;
};

export const formatManufacturerAddress = (
  address?: Manufacturer['address'],
) => {
  if (!address) {
    return '';
  }

  return [
    address.addressLine1,
    address.addressLine2,
    [address.city, address.state, address.zip].filter(Boolean).join(', '),
  ]
    .filter(Boolean)
    .join(', ');
};

export const applyManufacturerAddressToForm = (
  address?: Manufacturer['address'],
) => {
  if (!address) {
    return {
      manufacturerAddressLine1: '',
      manufacturerAddressLine2: '',
      manufacturerCity: '',
      manufacturerState: '',
      manufacturerZip: '',
      manufacturerAddress: '',
    };
  }

  const addressValues = {
    manufacturerAddressLine1: address.addressLine1 ?? '',
    manufacturerAddressLine2: address.addressLine2 ?? '',
    manufacturerCity: address.city ?? '',
    manufacturerState: address.state ?? '',
    manufacturerZip: address.zip ?? '',
  };

  return {
    ...addressValues,
    manufacturerAddress: formatManufacturerAddress(address),
  };
};

export const formatLocationAddress = (location?: Location) => {
  if (!location?.address) {
    return '';
  }

  const { address } = location;
  const fullAddress = (address as Location['address'] & {
    fullAddress?: string;
  }).fullAddress;

  if (fullAddress) {
    return fullAddress;
  }

  return [
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.state,
    address.zip,
  ]
    .filter(Boolean)
    .join(', ');
};

export const applyLocationAddressToForm = (location?: Location) => {
  if (!location?.address) {
    return {
      shipToAddressLine1: '',
      shipToAddressLine2: '',
      shipToCity: '',
      shipToState: '',
      shipToZip: '',
      liftGateRequired: false,
      shippingNotes: '',
    };
  }

  const { address } = location;

  return {
    shipToAddressLine1: address.addressLine1 ?? '',
    shipToAddressLine2: address.addressLine2 ?? '',
    shipToCity: address.city ?? '',
    shipToState: address.state ?? '',
    shipToZip: address.zip ?? '',
    liftGateRequired: location.liftGateRequired ?? false,
    shippingNotes: location.purchaseOrderShippingNotes ?? '',
  };
};

export const getInitialValues = (): CreatePurchaseOrderFormValues => ({
  date: new Date(),
  manufacturerId: '',
  manufacturerAddress: '',
  manufacturerAddressLine1: '',
  manufacturerAddressLine2: '',
  manufacturerCity: '',
  manufacturerState: '',
  manufacturerZip: '',
  manufacturerContact: '',
  destinationHubId: '',
  manualDestinationHubName: '',
  isManualDestinationHub: false,
  shipToAddressLine1: '',
  shipToAddressLine2: '',
  shipToCity: '',
  shipToState: '',
  shipToZip: '',
  shipToContactId: '',
  manualShipToContact: '',
  isManualShipToContact: false,
  poNumber: '',
  liftGateRequired: false,
  shippingNotes: '',
  shippingCost: 0,
  tax: 0,
  costAdjustment: 0,
  skuLines: [getEmptySkuLine()],
});

export const mapPurchaseOrderToFormValues = (
  purchaseOrder: PurchaseOrder,
  context: {
    manufacturer?: Manufacturer;
    location?: Location;
    skus: SKU[];
    arts: Art[];
    washHubAdmins?: Array<{ _id: string }>;
  },
): CreatePurchaseOrderFormValues => {
  const isManualDestinationHub = !purchaseOrder.destinationLocationId;
  const manufacturerAddress = applyManufacturerAddressToForm(
    context.manufacturer?.address,
  );
  const locationAddress = isManualDestinationHub
    ? {
        shipToAddressLine1: '',
        shipToAddressLine2: '',
        shipToCity: '',
        shipToState: '',
        shipToZip: '',
        liftGateRequired: false,
        shippingNotes: '',
      }
    : applyLocationAddressToForm(context.location);

  const skuLines = (purchaseOrder.orderDetails ?? []).map((detail) => {
    const sku = context.skus.find((item) => item._id === detail.skuId);
    const art = context.arts.find((item) => item._id === detail.artId);

    return applySkuLineCalculations(
      {
        skuId: detail.skuId,
        artNumber: art?.number ?? detail.art?.number ?? '',
        artId: detail.artId,
        artDescription: art?.description ?? detail.art?.description ?? '',
        boxQty: detail.boxQuantity,
        totalUnits: detail.itemQuantity,
        costPerBox: '',
        costPerUnit: '',
        totalCost: '',
      },
      sku,
      'boxQty',
    );
  });

  return {
    ...getInitialValues(),
    date:
      toPurchaseOrderDate(purchaseOrder.date) ??
      toPurchaseOrderDate(purchaseOrder.createdAt) ??
      new Date(),
    manufacturerId: purchaseOrder.manufacturerId ?? '',
    ...manufacturerAddress,
    manufacturerContact: formatManufacturerEmails(
      getPurchaseOrderManufacturerEmails(
        purchaseOrder,
        context.manufacturer?.email,
      ),
    ),
    destinationHubId: purchaseOrder.destinationLocationId ?? '',
    manualDestinationHubName: isManualDestinationHub
      ? purchaseOrder.location ?? ''
      : '',
    isManualDestinationHub,
    ...locationAddress,
    shipToContactId: context.washHubAdmins?.[0]?._id ?? '',
    liftGateRequired:
      purchaseOrder.liftGateRequired ?? locationAddress.liftGateRequired,
    shippingNotes: purchaseOrder.shippingNotes ?? locationAddress.shippingNotes,
    poNumber: purchaseOrder.poNumer ?? '',
    shippingCost: purchaseOrder.shippingCost ?? 0,
    tax: purchaseOrder.tax ?? 0,
    costAdjustment: purchaseOrder.costAdjustment ?? 0,
    skuLines: skuLines.length > 0 ? skuLines : [getEmptySkuLine()],
  };
};
